import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

type AccountType = 'retail' | 'wholesale';
type AuthMode = 'login' | 'register' | 'otp';

export default function Auth() {
  const navigate = useNavigate();
  const [authMode, setAuthMode] = useState<AuthMode>('login');
  const [accountType, setAccountType] = useState<AccountType>('retail');
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [otp, setOtp] = useState(['', '', '', '', '', '']);

  const handleOtpChange = (index: number, value: string) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      if (value && index < 5) {
        const nextInput = document.getElementById(`otp-${index + 1}`);
        nextInput?.focus();
      }
    }
  };

  const handleVerify = () => {
    navigate('/home');
  };

  const handleRegister = () => {
    setAuthMode('otp');
  };

  const handleLogin = () => {
    navigate('/home');
  };

  const handleGuestLogin = () => {
    // تخزين حالة الزائر
    localStorage.setItem('userType', 'guest');
    navigate('/home');
  };

  if (authMode === 'otp') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F6F6F6] to-white flex flex-col" style={{ fontFamily: 'Cairo, sans-serif' }}>
        {/* Header */}
        <div className="bg-gradient-to-r from-[#0D47A1] to-[#1976D2] pt-12 pb-24 px-6 rounded-b-[2rem]">
          <button onClick={() => setAuthMode('register')} className="text-white mb-8">
            <i className="ri-arrow-right-line text-2xl"></i>
          </button>
          <h1 className="text-white text-2xl font-bold mb-2">تفعيل الحساب</h1>
          <p className="text-white/80 text-sm">أدخل الرمز المرسل إلى {formData.phone}</p>
        </div>

        {/* Content */}
        <div className="flex-1 -mt-16 px-6">
          <div className="bg-white rounded-2xl shadow-lg p-6">
            {/* OTP Inputs */}
            <div className="flex gap-2 justify-center mb-8" dir="ltr">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  id={`otp-${index}`}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  className="w-12 h-14 text-center text-xl font-bold border-2 border-gray-200 rounded-xl focus:border-[#0D47A1] focus:outline-none transition-colors"
                />
              ))}
            </div>

            {/* Resend */}
            <button className="text-[#0D47A1] text-sm font-medium mb-6 w-full text-center">
              إعادة إرسال الرمز
            </button>

            {/* Verify Button */}
            <button
              onClick={handleVerify}
              className="w-full bg-gradient-to-r from-[#0D47A1] to-[#1976D2] text-white py-4 rounded-xl font-bold text-lg shadow-lg active:scale-95 transition-transform"
            >
              تفعيل
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F6F6F6] to-white flex flex-col" style={{ fontFamily: 'Cairo, sans-serif' }}>
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0D47A1] to-[#1976D2] pt-12 pb-24 px-6 rounded-b-[2rem]">
        <h1 className="text-white text-2xl font-bold mb-2">
          {authMode === 'login' ? 'تسجيل الدخول' : 'إنشاء حساب'}
        </h1>
        <p className="text-white/80 text-sm">
          {authMode === 'login' ? 'مرحباً بعودتك' : 'انضم إلينا الآن'}
        </p>
      </div>

      {/* Content */}
      <div className="flex-1 -mt-16 px-6">
        <div className="bg-white rounded-2xl shadow-lg p-6">
          {authMode === 'register' && (
            <>
              {/* Account Type Selection */}
              <div className="mb-6">
                <label className="text-gray-700 text-sm font-medium mb-3 block">نوع الحساب</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setAccountType('retail')}
                    className={`py-4 rounded-xl font-medium transition-all ${
                      accountType === 'retail'
                        ? 'bg-[#0D47A1] text-white shadow-lg'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    <i className="ri-user-line text-xl mb-1 block"></i>
                    عميل تجزئة
                  </button>
                  <button
                    onClick={() => setAccountType('wholesale')}
                    className={`py-4 rounded-xl font-medium transition-all ${
                      accountType === 'wholesale'
                        ? 'bg-[#0D47A1] text-white shadow-lg'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    <i className="ri-store-line text-xl mb-1 block"></i>
                    تاجر جملة
                  </button>
                </div>
              </div>

              {/* Name Input */}
              <div className="mb-4">
                <label className="text-gray-700 text-sm font-medium mb-2 block">الاسم</label>
                <div className="relative">
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full pr-12 pl-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#0D47A1] focus:outline-none transition-colors"
                    placeholder="أدخل اسمك"
                  />
                  <i className="ri-user-line absolute right-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
                </div>
              </div>
            </>
          )}

          {/* Phone Input */}
          <div className="mb-4">
            <label className="text-gray-700 text-sm font-medium mb-2 block">رقم الجوال</label>
            <div className="relative">
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full pr-12 pl-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#0D47A1] focus:outline-none transition-colors"
                placeholder="05xxxxxxxx"
                dir="ltr"
              />
              <i className="ri-phone-line absolute right-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
            </div>
          </div>

          {authMode === 'register' && (
            <div className="mb-4">
              <label className="text-gray-700 text-sm font-medium mb-2 block">البريد الإلكتروني</label>
              <div className="relative">
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full pr-12 pl-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#0D47A1] focus:outline-none transition-colors"
                  placeholder="example@email.com"
                  dir="ltr"
                />
                <i className="ri-mail-line absolute right-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
              </div>
            </div>
          )}

          {/* Password Input */}
          <div className="mb-4">
            <label className="text-gray-700 text-sm font-medium mb-2 block">كلمة المرور</label>
            <div className="relative">
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full pr-12 pl-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#0D47A1] focus:outline-none transition-colors"
                placeholder="••••••••"
              />
              <i className="ri-lock-line absolute right-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
            </div>
          </div>

          {authMode === 'register' && (
            <div className="mb-6">
              <label className="text-gray-700 text-sm font-medium mb-2 block">تأكيد كلمة المرور</label>
              <div className="relative">
                <input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className="w-full pr-12 pl-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#0D47A1] focus:outline-none transition-colors"
                  placeholder="••••••••"
                />
                <i className="ri-lock-line absolute right-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            onClick={authMode === 'login' ? handleLogin : handleRegister}
            className="w-full bg-gradient-to-r from-[#0D47A1] to-[#1976D2] text-white py-4 rounded-xl font-bold text-lg shadow-lg active:scale-95 transition-transform mb-4"
          >
            {authMode === 'login' ? 'تسجيل الدخول' : 'إنشاء حساب'}
          </button>

          {/* Guest Login Button */}
          <button
            onClick={handleGuestLogin}
            className="w-full bg-white border-2 border-[#0D47A1] text-[#0D47A1] py-4 rounded-xl font-bold text-lg active:scale-95 transition-transform mb-4 flex items-center justify-center gap-2"
          >
            <i className="ri-user-smile-line text-xl"></i>
            الدخول كزائر
          </button>

          {/* Toggle Auth Mode */}
          <div className="text-center">
            <button
              onClick={() => setAuthMode(authMode === 'login' ? 'register' : 'login')}
              className="text-[#0D47A1] text-sm font-medium"
            >
              {authMode === 'login' ? 'ليس لديك حساب؟ سجل الآن' : 'لديك حساب؟ سجل دخولك'}
            </button>
          </div>
        </div>

        {/* Language Selector */}
        <div className="mt-6 flex justify-center gap-4">
          <button className="px-6 py-2 bg-white rounded-full shadow text-sm font-medium text-[#0D47A1]">
            العربية
          </button>
          <button className="px-6 py-2 bg-white/50 rounded-full text-sm font-medium text-gray-600">
            English
          </button>
        </div>
      </div>
    </div>
  );
}